<template>
  <div id="knowledgebase-page" class="d-flex">
    <!-- Sidebar -->
    <aside class="bg-light border-right p-3" style="width: 250px; overflow-y: auto;">

      <!-- Search box -->
      <div class="mb-4 position-relative">
        <h5 class="mb-3">Search</h5>
        <div class="input-group">
          <input 
            type="text" 
            class="form-control" 
            v-model="searchQuery" 
            placeholder="Search..." 
            @keyup.enter="performSearch"
            @input="debouncedGetSuggestions"
            @blur="hideSuggestionsDelayed"
            @focus="showSuggestions"
          >
          <div class="input-group-append">
            <button class="btn text-white" style="background-color: #001344" @click="performSearch">
              <i class="fa fa-search"></i>
            </button>
          </div>
        </div>
        
        <!-- Suggestions dropdown -->
        <div v-if="showingSuggestions && suggestions.length > 0" 
             class="suggestions-dropdown">
          <div v-for="suggestion in suggestions" 
               :key="suggestion.id" 
               class="suggestion-item"
               @mousedown="selectSuggestion(suggestion)">
            <div class="d-flex justify-content-between align-items-start flex-column">
              <span class="suggestion-title">{{ suggestion.title }}</span>
              <span class="suggestion-category align-self-end">{{ suggestion.category_display_name }}</span>
            </div>
          </div>
        </div>
      </div>

      <h5 class="mb-3">Categories</h5>
      <div v-if="!categories.length" class="text-center">
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
      <ul class="list-unstyled" v-else>
        <li v-for="category in categories" :key="category.category" :class="{ 'mt-2': category.subcategory === '', 'ml-2': category.subcategory !== '' }">
          <button
            class="btn p-0"
            :class="{ 'font-weight-bold': category.subcategory === '' }"
            style="text-align: left" 
            :style="{ 
              color: (selectedSubcategory && selectedSubcategory === category.subcategory) || (category.subcategory === '' && selectedCategory === category.category) ? '#E30118' : 'inherit', 
              fontSize: category.subcategory === '' ? 'inherit' : '14px',
             }"            
            @click="selectCategory(category.category, category.subcategory)"
          >
            {{ category.subcategory ? category.subcategory_display_name : category.category_display_name }}
          </button>
        </li>
      </ul>
      <a href="/coraa" target="_blank" class="btn text-white p-2" style="background-color: #001344">Admin</a>
    </aside>

    <!-- Main content -->
    <main class="flex-grow-1 p-4">
      <div v-if="loading" class="text-center">
        <div class="spinner-border" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
      <div v-else>
        <div v-if="isSearching">
          <h4 class="mb-4">Search Results for "{{ searchQuery }}"</h4>
          <div v-if="searchResults.length === 0" class="alert alert-info">
            No results found for "{{ searchQuery }}"
          </div>
          <div v-else>
            <div v-for="(items, category) in groupedSearchResults" :key="category" class="mb-4">
              <h5>{{ category }}</h5>
              <ul class="list-unstyled">
                <li v-for="item in items" :key="item.id" class="mb-2 d-flex align-items-center">
                  <a 
                    :href="item.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-decoration-none mr-2"
                    style="color: #001344"
                  >
                    {{ item.title }}
                  </a>
                  <button 
                    class="btn btn-sm p-0 ml-2" 
                    @click="openEditModal(item.id)"
                    title="Edit Content"
                  >
                    <i class="fa fa-edit" style="color: #001344"></i>
                  </button>
                  
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div v-else>
          <h4 class="mb-4">{{ selectedSubcategoryName || selectedCategoryName }}</h4>
          <div v-if="items.length === 0" class="alert alert-info">
            No items found in this category
          </div>
          <ul class="list-unstyled" v-else>
            <template v-for="(item, index) in items">
              <h4 v-if="index !== 0 && item.subcategory !== items[index-1].subcategory"
                    class="mb-2 mt-4" :key="item.id">{{ item.subcategory_display_name }}</h4>
              <li class="mb-2 d-flex align-items-center">
                <a
                  :href="item.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  style="color: #001344"
                  class="text-decoration-none mr-2"
                >
                  {{ item.title }}
                </a>
                <button 
                  class="btn btn-sm p-0 ml-2" 
                  style="color: #001344"
                  @click="openEditModal(item.id)"
                  title="Edit Content"
                >
                  <i class="fa fa-edit"></i>
                </button>
              </li>
            </template>
          </ul>
        </div>
      </div>
    </main>

    <!-- Edit Modal -->
    <div class="modal fade" id="editContentModal" tabindex="-1" role="dialog" aria-labelledby="editContentModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editContentModalLabel">Edit Content: {{ currentItem ? currentItem.title : '' }}</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="modalLoading" class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
              </div>
            </div>
            <div v-else>
              <div class="form-group">
                <label for="searchableContent">Searchable Content</label>
                <textarea 
                  id="searchableContent" 
                  class="form-control" 
                  v-model="editContent" 
                  rows="15"
                ></textarea>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">Close</button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="saveContent" 
              :disabled="modalLoading || savingContent"
            >
              <span v-if="savingContent">
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Saving...
              </span>
              <span v-else>Save changes</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'

// State variables
const categories = ref([])
const selectedCategory = ref('')
const selectedCategoryName = ref('')
const selectedSubcategory = ref('')
const selectedSubcategoryName = ref('')
const items = ref([])
const loading = ref(true)
const searchQuery = ref('')
const searchResults = ref([])
const isSearching = ref(false)

// Suggestions state
const suggestions = ref([])
const showingSuggestions = ref(false)
const suggestionsTimeout = ref(null)
const debounceTimeout = ref(null)

// Modal state
const currentItem = ref(null)
const editContent = ref('')
const modalLoading = ref(false)
const savingContent = ref(false)

// Fetch categories on component mount
onMounted(async () => {
  try {
    loading.value = true
    const response = await axios.get('/api/knowledgebase/categories')
    const data = response.data;
    const result = []
  
    data.forEach((cat, i) => {
      const prevCat = i === 0 ? null : data[i-1].category;
      if ((i === 0 && cat.subcategory !== '') || (prevCat !== cat.category && cat.subcategory !== '')) {
        result.push({
          category: cat.category,
          category_display_name: cat.category_display_name,
          subcategory: '',
          subcategory_display_name: '',
        });
      }

      result.push(cat);
    });

    categories.value = result
  
    // Select first category by default if available
    if (categories.value.length > 0) {
      selectCategory(categories.value[0].category)
    } else {
      loading.value = false
    }
  } catch (error) {
    console.error('Error fetching categories:', error)
    loading.value = false
  }
})

// Debounced function to get suggestions
function debouncedGetSuggestions() {
  // Clear any existing timeout
  if (debounceTimeout.value) {
    clearTimeout(debounceTimeout.value)
  }
  
  // Set a new timeout
  debounceTimeout.value = setTimeout(() => {
    if (searchQuery.value.length >= 2) {
      getSuggestions()
    } else {
      suggestions.value = []
      showingSuggestions.value = false
    }
  }, 500)
}

// Get suggestions from API
async function getSuggestions() {
  try {
    const response = await axios.get('/api/knowledgebase/suggestions', {
      params: { query: searchQuery.value }
    })
    
    suggestions.value = response.data
    showingSuggestions.value = true
  } catch (error) {
    console.error('Error fetching suggestions:', error)
    suggestions.value = []
  }
}

// Show suggestions dropdown
function showSuggestions() {
  if (suggestions.value.length > 0) {
    showingSuggestions.value = true
  }
}

// Hide suggestions dropdown with a small delay to allow for clicks
function hideSuggestionsDelayed() {
  //suggestionsTimeout.value = setTimeout(() => {
  //  showingSuggestions.value = false
  //}, 200)
}

// Select a suggestion
function selectSuggestion(suggestion) {
  showingSuggestions.value = false
  
  // Navigate to the item's URL
  window.open(suggestion.url, '_blank')
}

// Select a category and fetch its items
async function selectCategory(category, subcategory) {
  try {
    loading.value = true
    isSearching.value = false

    selectedCategory.value = ''
    selectedCategoryName.value = ''
    selectedSubcategory.value = ''
    selectedSubcategoryName.value = ''

    if (subcategory) {
      selectedSubcategory.value = subcategory;
      const categoryObj = categories.value.find(c => c.subcategory === subcategory)
      selectedSubcategoryName.value = categoryObj ? categoryObj.subcategory_display_name : ''
    }
    else {
      selectedCategory.value = category
      const categoryObj = categories.value.find(c => c.category === category)
      selectedCategoryName.value = categoryObj ? categoryObj.category_display_name : ''
    }
    
    const response = await axios.get(`/api/knowledgebase/category/${subcategory ? subcategory : category}`)
    items.value = response.data
    loading.value = false
  } catch (error) {
    console.error('Error fetching items:', error)
    loading.value = false
  }
}

// Perform search
async function performSearch() {
  if (!searchQuery.value.trim()) {
    // If search query is empty, go back to category view
    if (selectedCategory.value) {
      await selectCategory(selectedCategory.value)
    }
    return
  }

  try {
    loading.value = true
    isSearching.value = true
  
    const response = await axios.get('/api/knowledgebase/search', {
      params: { query: searchQuery.value }
    })
  
    searchResults.value = response.data
    loading.value = false
  } catch (error) {
    console.error('Error searching:', error)
    loading.value = false
  }
}

// Group search results by category for display
const groupedSearchResults = computed(() => {
  const grouped = {}

  searchResults.value.forEach(item => {
    if (!grouped[item.category_display_name]) {
      grouped[item.category_display_name] = []
    }
    grouped[item.category_display_name].push(item)
  })

  return grouped
})

// Open edit modal
async function openEditModal(id) {
  modalLoading.value = true
  currentItem.value = null
  editContent.value = ''
  
  // Show the modal using Bootstrap's jQuery API
  $('#editContentModal').modal('show')
  
  try {
    const response = await axios.get(`/api/knowledgebase/item/${id}`)
    currentItem.value = response.data
    editContent.value = response.data.searchable_content || ''
    modalLoading.value = false
  } catch (error) {
    console.error('Error fetching item content:', error)
    modalLoading.value = false
    // Show error message
    alert('Failed to load content. Please try again.')
  }
}

// Save content
async function saveContent() {
  if (!currentItem.value) return
  
  savingContent.value = true
  
  try {
    await axios.put(`/api/knowledgebase/item/${currentItem.value.id}`, {
      searchable_content: editContent.value
    })
    
    savingContent.value = false
    
    // Close the modal
    $('#editContentModal').modal('hide')
    
    // Refresh the current view
    if (isSearching.value) {
      await performSearch()
    } else {
      await selectCategory(selectedCategory.value)
    }
    
  } catch (error) {
    console.error('Error saving content:', error)
    savingContent.value = false
    alert('Failed to save content. Please try again.')
  }
}
</script>

<style scoped>
button.active {
  color: #007bff;
  text-decoration: underline;
}
a {
  color: #0066cc;
  text-decoration: underline;
}
.spinner-border {
  width: 1.5rem;
  height: 1.5rem;
}

/* Suggestions dropdown styling */
.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.suggestion-item {
  padding: 10px 15px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover {
  background-color: #f8f9fa;
}

.suggestion-title {
  font-size: 14px;
  color: #001344;
}

.suggestion-category {
  font-size: 11px;
  color: #6c757d;
  text-transform: uppercase;
  margin-left: 8px;
}
</style>